/**
 * Ledges (DESIGN.md 4.9): the grab, the hang, and the mantle. A ledge is the top corner of any
 * plain solid (a wall, a tier, a platform's edge; boxes and pipes have their own moves). With the
 * arms out, a hand that comes within its reach radius of the corner while the body is in the air
 * below it takes hold, and the body hangs against the wall face on straight arms: the mass a fixed
 * distance under the hands (the same arm and torso lengths the bar hangs by), the side of the box
 * ledgeGap off the face, eased onto that point over the attach ticks the way a catch eases onto its
 * length. A tuck lifts the feet as it does anywhere the mass is held; the hang itself is a rest state
 * with no swing, and the body stands upright in it.
 *
 * From the hang, Jump or Pull is the mantle. The owner asked for the jump ("pull yourself up onto
 * tall walls by grabbing then jumping", 2026-09-25); the design's verb is Pull, the pull-up, and both
 * fire on the same window, like the ladder's kip and pull. On a pad the left stick pushed up is a
 * jump, so it climbs too, which is the design's auto-mantle assist for free. Two presses by design
 * (the owner, the same day): a first timer stands at the foot of the wall, holds Reach, jumps to take
 * the edge, and jumps again to climb; the run at the wall with the press timed to the grab is the
 * fast way, as the run, jump, and tuck is over a tier. The mantle is graded on how quickly it follows
 * the grab, PERFECT with no pause (a jump buffered just before the hands arrive counts as no pause),
 * and its grade decides the speed the body comes over the edge with: the
 * horizontal speed it arrived with, kept in full at PERFECT with a small boost, less at GOOD and OK,
 * and never under mantleExitSpeed so a mantle from a dead hang still lands on top. The body's motion
 * over the edge is the vault's plant machinery (`hurdles.ts`, `PlantPhase.Mantling`): the arms pull it
 * straight up the face, then the hands press it up and over, the lower body passing the corner while
 * the hands hold the ledge as a vault's does, and it comes down on the top. Letting go of Reach drops.
 */

import { pinAttitudeUpright } from "../attitude";
import { comHeight } from "../body";
import type { EventBuffer } from "../events";
import { abs, sqrt } from "../fastmath";
import { emitGrade, gradeOffset } from "../grades";
import { linkOnLand } from "../link";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { DT, Ev, Grade, GradeKind, HZ, S, SolidClass } from "../types";
import type { World } from "../world";
import { PlantPhase } from "./hurdles";
import { setState } from "./transitions";

/** How far the hanging mass sits under the corner, m: straight arms plus the torso, less the offset off the face. */
export function hangDrop(t: CompiledTuning): number {
  const L = t.armLength + t.torsoOffset;
  const dx = t.capsuleRadius + t.ledgeGap;
  const d2 = L * L - dx * dx;
  return d2 > 0 ? sqrt(d2) : 0;
}

/** The face of solid `i` the body hangs on: its left face when the wall is at +x of the body, else its right. */
export function ledgeFaceX(world: World, i: number, dir: number): number {
  return dir > 0 ? world.solidX0[i]! : world.solidX1[i]!;
}

/** The hanging body's x: its side ledgeGap off the face. */
export function ledgeHangX(world: World, t: CompiledTuning, i: number, dir: number): number {
  return ledgeFaceX(world, i, dir) - dir * (t.capsuleRadius + t.ledgeGap);
}

/** The hanging body's feet for a tuck amount: the mass holds under the corner and the feet come up with the tuck. */
export function ledgeHangY(world: World, t: CompiledTuning, i: number, tuck: number): number {
  return world.solidY1[i]! - hangDrop(t) - comHeight(t, tuck);
}

/**
 * Whether the top corner of solid `i` on side `dir` is a ledge the body can hang from: standing room
 * on the top past the edge, and a standing body hanging below the corner clear of every solid (a
 * corner too low over a floor is not a ledge, and is jumped instead).
 */
function ledgeFits(world: World, t: CompiledTuning, i: number, dir: number): boolean {
  const r = t.capsuleRadius;
  const top = world.solidY1[i]!;
  const face = ledgeFaceX(world, i, dir);
  const lx0 = dir > 0 ? face : face - 2 * r;
  if (world.roomAbove(lx0, lx0 + 2 * r, top) < t.standHeight) return false;
  const hx = ledgeHangX(world, t, i, dir);
  const feet = ledgeHangY(world, t, i, 0);
  return world.overlapSolid(hx - r, feet, hx + r, feet + t.standHeight) < 0;
}

/**
 * Take a ledge if the arms are out and a hand is within its reach radius of a solid's top corner,
 * with the body in the air beside that face and its shoulders below the top. The nearest such
 * corner wins. Called after the move and after the bar grip has had its chance.
 */
export function tryLedge(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer): boolean {
  if (s.ledgeSolid >= 0 || s.barId >= 0 || s.grounded === 1 || s.planted !== PlantPhase.None) return false;
  if (s.armsOut !== 1 || s.reach < t.gripRelease) return false;
  const r = t.capsuleRadius;
  let best = -1;
  let bestDir = 1;
  let bestD2 = s.reachRadius * s.reachRadius;
  for (let i = 0; i < world.solidCount; i++) {
    if (world.solidClass[i] !== SolidClass.Solid) continue;
    if (i === s.lastLedge && s.ledgeLockout > 0) continue;
    const top = world.solidY1[i]!;
    if (s.shoulderY >= top) continue;
    let dir = 0;
    if (s.x + r <= world.solidX0[i]! + 1e-6) dir = 1;
    else if (s.x - r >= world.solidX1[i]! - 1e-6) dir = -1;
    else continue;
    const dx = ledgeFaceX(world, i, dir) - s.handX;
    const dy = top - s.handY;
    const d2 = dx * dx + dy * dy;
    if (d2 > bestD2) continue;
    if (!ledgeFits(world, t, i, dir)) continue;
    bestD2 = d2;
    best = i;
    bestDir = dir;
  }
  if (best < 0) return false;
  grab(s, t, ev, best, bestDir);
  return true;
}

/** The grab: the hands close on the corner, the arms stop the body, the hang eases in from here. */
function grab(s: SimState, t: CompiledTuning, ev: EventBuffer, i: number, dir: number): void {
  // The flight ended on something that is not a bar: a lache run ends here with its count standing.
  linkOnLand(s, ev);
  s.linkOpen = 0;
  s.ledgeSolid = i;
  s.ledgeDir = dir;
  s.ledgeEntrySpeed = abs(s.vx);
  s.ledgeGrabX = s.x;
  s.ledgeGrabY = s.y;
  s.attachLeft = t.attachTicks;
  s.attachTotal = t.attachTicks;
  s.attachPhi0 = s.phi;
  s.facing = dir;
  s.coyote = 0;
  s.slideCoyote = 0;
  s.jumpCutArmed = 0;
  s.tumbling = 0;
  s.ax = -s.vx / DT;
  s.ay = -s.vy / DT;
  s.vx = 0;
  s.vy = 0;
  ev.push(Ev.LedgeGrabbed, s.tick, i, s.ledgeEntrySpeed, dir);
  setState(s, ev, S.Ledge);
  // A jump pressed in the buffer time before the hands arrived is a mantle with no pause at all.
  if (s.buffer > 0) {
    s.buffer = 0;
    mantle(s, t, ev, 0);
  }
}

/**
 * One tick hanging from a ledge: Reach let go drops, Jump or Pull mantles, otherwise the body eases
 * onto the hang point over the attach ticks and holds there (kinematic, so the renderer and the
 * continuity checks see the motion), upright.
 */
export function tickLedge(
  world: World,
  s: SimState,
  t: CompiledTuning,
  ev: EventBuffer,
  jumpPressed: boolean,
  pullPressed: boolean,
): void {
  if (s.reach < t.gripRelease) {
    drop(s, t, ev);
    return;
  }
  if (jumpPressed || pullPressed || s.buffer > 0) {
    s.buffer = 0;
    mantle(s, t, ev, s.stateTicks);
    return;
  }
  const i = s.ledgeSolid;
  const hx = ledgeHangX(world, t, i, s.ledgeDir);
  const hy = ledgeHangY(world, t, i, s.tuck);
  let x = hx;
  let y = hy;
  if (s.attachLeft > 0) {
    s.attachLeft--;
    const f = s.attachTotal > 0 ? s.attachLeft / s.attachTotal : 0;
    x = hx + (s.ledgeGrabX - hx) * f;
    y = hy + (s.ledgeGrabY - hy) * f;
  }
  const vx = (x - s.x) / DT;
  const vy = (y - s.y) / DT;
  s.ax = (vx - s.vx) / DT;
  s.ay = (vy - s.vy) / DT;
  s.vx = vx;
  s.vy = vy;
  s.x = x;
  s.y = y;
  // The stick and the tilt do nothing on a ledge in a side view (no shimmy, no lean).
  pinAttitudeUpright(s);
}

/** Reach let go: the hands open and the body falls from where it hangs. */
function drop(s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  ev.push(Ev.LedgeDropped, s.tick, s.ledgeSolid);
  s.lastLedge = s.ledgeSolid;
  s.ledgeLockout = t.regrabTicks;
  s.ledgeSolid = -1;
  s.attachLeft = 0;
  s.grounded = 0;
  s.coyote = 0;
  s.jumpCutArmed = 0;
  setState(s, ev, S.Fall);
}

/**
 * The mantle, `delayTicks` after the grab: graded on that delay, the exit speed set by the grade,
 * and the body handed to the plant machinery to go up the face and over the edge. The pull starts
 * at mantlePullSpeed, or faster if the body was still rising (a mantle with no pause keeps going).
 */
function mantle(s: SimState, t: CompiledTuning, ev: EventBuffer, delayTicks: number): void {
  const i = s.ledgeSolid;
  const dir = s.ledgeDir;
  const ms = (delayTicks * 1000) / HZ;
  const grade = gradeOffset(ms, t.mantlePerfectMs, 0, t.mantleGoodMs);
  const retain = grade === Grade.Perfect ? 1 : grade === Grade.Good ? t.mantleRetainGood : t.mantleRetainOk;
  let exit = s.ledgeEntrySpeed * retain + (grade === Grade.Perfect ? t.mantlePerfectBoost : 0);
  if (exit < t.mantleExitSpeed) exit = t.mantleExitSpeed;
  const vy = s.vy > t.mantlePullSpeed ? s.vy : t.mantlePullSpeed;
  s.ax = -s.vx / DT;
  s.ay = (vy - s.vy) / DT;
  s.vx = 0;
  s.vy = vy;
  s.ledgeSolid = -1;
  s.lastLedge = i;
  s.ledgeLockout = t.regrabTicks;
  s.attachLeft = 0;
  s.plantBox = i;
  s.planted = PlantPhase.Mantling;
  s.plantHands = 1;
  s.plantTargetVx = dir * exit;
  s.plantTicks = 0;
  s.mantling = 1;
  s.vaultArmBox = -1;
  s.facing = dir;
  s.grounded = 0;
  s.coyote = 0;
  s.jumpCutArmed = 0;
  s.tumbling = 0;
  s.lastMantleGrade = grade;
  ev.push(Ev.Mantled, s.tick, i, grade, ms);
  emitGrade(s, t, ev, GradeKind.Mantle, grade, ms);
  setState(s, ev, S.Plant);
}

/** Count down the regrab lockout of the last ledge while not on one. */
export function tickLedgeLockout(s: SimState): void {
  if (s.ledgeLockout > 0) s.ledgeLockout--;
}
